import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { useConfigStore } from '../store/configStore';
import LoginRequired from '../components/LoginRequired';
import { useNavigate } from 'react-router-dom';

interface GenerationJob {
  id: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'saved';
  modelId?: string;
  error?: string;
}

export default function GeneratePage() {
  const { isAuthenticated } = useAuthStore();
  const generation = useConfigStore((s) => s.generation);
  const configLoaded = useConfigStore((s) => s.loaded);
  const fetchConfig = useConfigStore((s) => s.fetchConfig);
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [isPublic, setIsPublic] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [job, setJob] = useState<GenerationJob | null>(null);

  useEffect(() => {
    if (!configLoaded) fetchConfig();
  }, [configLoaded, fetchConfig]);

  useEffect(() => {
    if (!job || job.status === 'saved' || job.status === 'failed') return;

    const interval = setInterval(async () => {
      const { apiFetch } = await import('../lib/api');
      const res = await apiFetch(`/api/generate/${job.id}`);
      if (res.ok) {
        const data = (await res.json()) as GenerationJob;
        setJob(data);
        if (data.status === 'saved' && data.modelId) {
          clearInterval(interval);
          navigate(`/models/${data.modelId}`);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [job, navigate]);

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith('image/')) setImage(f);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const needsImage = generation.capabilities.image && !generation.capabilities.text;
    if (!prompt || (needsImage && !image)) return;

    setIsGenerating(true);
    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('isPublic', String(isPublic));
      if (image) formData.append('image', image);

      const { apiFetch } = await import('../lib/api');
      const res = await apiFetch('/api/generate', { method: 'POST', body: formData });
      const data = (await res.json()) as { error?: string; jobId?: string; status?: string };
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      if (data.jobId) {
        setJob({ id: data.jobId, status: data.status as GenerationJob['status'] || 'pending' });
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container" style={{ paddingTop: '1.5rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>Generate 3D Model</h2>
        <LoginRequired />
      </div>
    );
  }

  if (configLoaded && !generation.enabled) {
    return (
      <div className="container" style={{ paddingTop: '1.5rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>Generate 3D Model</h2>
        <div className="card" style={{ maxWidth: '640px' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.75rem' }}>
            3D generation is <strong>disabled</strong> in this deployment.
          </p>
          <p style={{ color: 'var(--muted)', lineHeight: 1.6 }}>
            This demo ships without a hosted generation backend. To enable it, run
            the self-hosted TripoSR sidecar on your own hardware and set{' '}
            <code>GENERATION_ENABLED=true</code> with{' '}
            <code>GENERATION_PROVIDER=local</code>. See{' '}
            <code>sidecar/README.md</code> in the repository for setup.
          </p>
        </div>
      </div>
    );
  }

  const imageOnly = generation.capabilities.image && !generation.capabilities.text;

  return (
    <div className="container" style={{ paddingTop: '1.5rem' }}>
      <h2 style={{ marginBottom: '1rem' }}>Generate 3D Model</h2>
      <div className="card" style={{ maxWidth: '640px' }}>
        {job ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ fontSize: '1.1rem' }}>Status: <strong>{job.status}</strong></p>
            {job.error && <p style={{ color: 'var(--danger)' }}>{job.error}</p>}
            {job.status !== 'saved' && job.status !== 'failed' && (
              <p style={{ color: 'var(--muted)' }}>Polling for updates...</p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: 0 }}>
              Provider: <strong>{generation.provider}</strong>
              {imageOnly && ' — image-to-3D only (a reference image is required)'}
            </p>
            <textarea
              placeholder={imageOnly ? 'Name this model...' : 'Describe the 3D model you want...'}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={imageOnly ? 2 : 4}
            />
            <label
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleImageDrop}
              style={{
                border: '2px dashed #475569',
                borderRadius: '0.5rem',
                padding: '1rem',
                textAlign: 'center',
                cursor: 'pointer',
                color: 'var(--muted)',
              }}
            >
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImage(e.target.files?.[0] || null)}
                style={{ display: 'none' }}
              />
              {image
                ? image.name
                : imageOnly
                  ? 'Drop a reference image or click to browse (required)'
                  : 'Drop a reference image or click to browse (optional)'}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              Make this model public
            </label>
            <button type="submit" disabled={!prompt || isGenerating || (imageOnly && !image)}>
              {isGenerating ? 'Generating...' : 'Generate'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
